import { DatePipe, NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, effect, QueryList, Signal, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, MatPaginatorSelectConfig } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatCell, MatCellDef, MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { AdminService } from '../../admin/admin.service';
import { FeatureFlagService } from '../../feature-flags/feature-flag.service';
import { TransformRunFrequency } from '../../kyloe-pipes.pipe';
import { DataToolsApps } from '../../shared/DataToolsApps';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import { DataRulesDataService } from '../data-rules-data.service';
import { ARCHIVE_FOLDER_NAME, IRuleStats, IRuleTag, ITaggedRule, RuleData, TaskState } from '../data-rules-model';
import { DataRulesService } from '../data-rules.service';
import { DataRulesTagService } from '../data-rules-folders-view/data-rules-folders/data-rules-tag.service';
import { ScheduleDataService } from '../rule/rule-schedule/schedule-data.service';
import { TasksDialogComponent } from './tasks-dialog/tasks-dialog.component';

@Component({
  selector: 'app-history',
  templateUrl: './data-rules-history.component.html',
  imports: [
    SkeletonComponent,
    MatPaginator,
    MatRow,
    MatHeaderRow,
    MatRowDef,
    MatHeaderRowDef,
    MatSortModule,
    MatIcon,
    MatTooltip,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCellDef,
    NgClass,
    NgIf,
    MatTable,
    MatSort,
    TransformRunFrequency,
    DatePipe
  ],
  styleUrls: ['./data-rules-history.component.scss']
})
export class DataRulesHistoryComponent implements AfterViewInit {
  TaskState: typeof TaskState = TaskState;    // so the template can access the enum values
  private readonly allColumns: string[] = ['name', 'entity', 'userId', 'userName', 'timesRun', 'state', 'startTime',
    'totalRecords', 'recordsProcessed', 'noUpdateRequired', 'recordsNotReached', 'recordsFailed', 'ruleLink'];
  // Starts without the flagged column, same as FeatureFlagDirective's default, since the flag signal
  // starts false and only flips to its real value once flags settle (loaded async during auth).
  displayedColumns: string[] = this.allColumns.filter(column => column !== 'recordsNotReached');
  statsDataSource: MatTableDataSource<IRuleStats> = new MatTableDataSource<IRuleStats>([]);
  loading = true;
  readonly pageSizeSelectConfig: MatPaginatorSelectConfig = { panelClass: 'kyloe-paginator-select-panel' };
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChildren('innerTable') innerTable: QueryList<MatTable<any>>;
  public DataToolsApps = DataToolsApps;
  ruleData: RuleData[];
  archivedRuleIds: Set<string> = new Set();
  schedulingEnabled: boolean = false;
  foldersEnabled: Signal<boolean>;
  recordUpdateLimitEnabled: Signal<boolean>;

  constructor(private dataRulesService: DataRulesService,
              private dataRulesDataService: DataRulesDataService,
              private dataRulesTagService: DataRulesTagService,
              private scheduleDataService: ScheduleDataService,
              private router: Router,
              private dialog: MatDialog,
              private adminService: AdminService,
              private featureFlagService: FeatureFlagService) {
    window.scrollTo(0, 0);
    this.foldersEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.dataRulesFolders);
    // enabled() returns a signal that starts false and flips once flags settle (loaded async during auth),
    // so the template reads foldersEnabled() to stay in sync instead of capturing the initial value.
    this.recordUpdateLimitEnabled = this.featureFlagService.enabled(FeatureFlagService.FLAGS.dataRulesRecordUpdateLimit);
    effect(() => {
      this.displayedColumns = this.recordUpdateLimitEnabled()
        ? this.allColumns
        : this.allColumns.filter(column => column !== 'recordsNotReached');
    });
    this.dataRulesDataService.initialize(true);
    const schedules$ = this.scheduleDataService.initialize(true);
    const ruleStats$ = this.dataRulesService.getRuleStats();
    const rules$ = this.dataRulesService.getAllRules();
    const tags$ = this.dataRulesTagService.getAllTags();
    const taggedRules$ = this.dataRulesTagService.getAllTaggedRules();
    forkJoin({schedule: schedules$, ruleStats: ruleStats$, rules: rules$, tags: tags$, taggedRules: taggedRules$})
      .subscribe(({ruleStats, rules, tags, taggedRules}) => {
        this.ruleData = rules;
        this.archivedRuleIds = this.buildArchivedRuleIds(tags, taggedRules);
        this.updateRuleStatus(ruleStats, this.ruleData, this.archivedRuleIds);
        this.statsDataSource = new MatTableDataSource<IRuleStats>(DataRulesHistoryComponent.sortStats(ruleStats));
        this.statsDataSource.paginator = this.paginator;
        this.statsDataSource.sort = this.sort;
        this.populateSchedules(ruleStats);
        this.loading = false;
      });
  }

  ngAfterViewInit() {
    if (this.adminService.isInLiteMode()) {
      this.statsDataSource = new MatTableDataSource<IRuleStats>([]);
      this.statsDataSource.paginator = this.paginator;
      this.statsDataSource.sort = this.sort;
      return;
    }
    if (this.adminService.checkAppEnabled(DataToolsApps.DATARULES_SCHEDULING)){
      this.schedulingEnabled = true;
    }
  }

  goToApp(pca: DataToolsApps) {
    this.router.navigate([`/${ pca.toString() }`]);
  }

  goToRule(stat: IRuleStats) {
    this.router.navigate(['/datarules'], { queryParams: { entity: stat.entity, rule: stat.ruleId } });
  }

  tasksDialog(ruleId: string, name: string, timesRun: number): void {
    console.log(`opening tasks for rule ${ruleId}`);
    const dialogRef = this.dialog.open(TasksDialogComponent, {
      maxWidth: '88vw',
      minWidth: '800px',
      height: 'auto',
      autoFocus: true,
      data: {ruleId: ruleId, name: name, timesRun: timesRun}
    });

    this.statsDataSource = new MatTableDataSource<IRuleStats>([]);
    this.loading = true;

    this.dataRulesService.getRuleStats().subscribe((stats: IRuleStats[]) => {
      this.updateRuleStatus(stats, this.ruleData, this.archivedRuleIds);
      this.statsDataSource = new MatTableDataSource<IRuleStats>(DataRulesHistoryComponent.sortStats(stats));
      this.statsDataSource.paginator = this.paginator;
      this.statsDataSource.sort = this.sort;
      this.populateSchedules(stats);
      this.loading = false;
    });
  }

  private populateSchedules(stats: IRuleStats[]) {
    const scheduleMap = {};
    this.scheduleDataService.allSchedules.forEach((schedule) => {
        scheduleMap[schedule.dataRulesID] = schedule.ruleExpression;
    });
    stats.forEach(stat => {
      stat.scheduledFrequency = scheduleMap[stat.ruleId];
    });
  }

  private buildArchivedRuleIds(tags: IRuleTag[], taggedRules: ITaggedRule[]): Set<string> {
    const archiveTag = tags.find(t => t.name === ARCHIVE_FOLDER_NAME);
    if (!archiveTag) {
      return new Set();
    }
    return new Set(taggedRules.filter(tr => tr.tagId === archiveTag.id).map(tr => tr.ruleId));
  }

  private updateRuleStatus(stats: IRuleStats[], rules: RuleData[], archivedRuleIds: Set<string>) {
    for (let i = 0; i < stats.length; i++) {
      const rule: RuleData = rules.find(r => ('' + r.id) === stats[i].ruleId);
      if (!rule) {
        stats[i].isDeleted = true;
      } else {
        stats[i].isArchived = archivedRuleIds.has(stats[i].ruleId);
      }
    }
  }

  public getStatDisplayName(stat: IRuleStats) {
    return !stat.isDeleted ? stat.name : 'Deleted: ' + stat.name;
  }

  private static sortStats(stats: IRuleStats[]): IRuleStats[] {
    return stats.sort((a, b) => {
      if (this.stateRank(a.state) > this.stateRank(b.state)) {
        return 1;
      }
      if (this.stateRank(a.state) < this.stateRank(b.state)) {
        return -1;
      }

      if (!a.startTime) {
        return -1;
      }

      if (!b.startTime || a.startTime < b.startTime) {
        return 1;
      }
      if (a.startTime > b.startTime) {
        return -1;
      }
      return 0;
    });
  }

  private static stateRank(state: TaskState): number {
    switch (state) {
      case TaskState.SUBMITTED:
        return 0;
      case TaskState.QUEUED:
        return 1;
      case TaskState.PRE_PROCESS:
        return 2;
      default:
        return 3;
    }
  }
}
